// src/pages/GlobalSettings.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  School,
  DollarSign,
  BookOpen,
  Users,
  Mail,
  Shield,
  RefreshCw,
  Save,
  Settings,
} from "lucide-react";
import { globalSettingsService } from "../services/global-settings";
import { GlobalSettings } from "../types/global-settings";
import ServiceWorkerSettings from "../components/settings/ServiceWorkerSettings";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";

const GlobalSettingsPage: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("school");
  const [settings, setSettings] = useState<GlobalSettings | null>(null);

  const tabs = [
    { id: "school", label: "School Information", icon: School },
    { id: "academic", label: "Academic Year", icon: BookOpen },
    { id: "financial", label: "Financial Settings", icon: DollarSign },
    { id: "academic-config", label: "Academic Configuration", icon: BookOpen },
    { id: "contacts", label: "Contact Information", icon: Users },
    { id: "system", label: "System Settings", icon: Shield },
    { id: "notifications", label: "Notifications", icon: Mail },
    { id: "app-cache", label: "App Cache & Updates", icon: Settings },
  ];

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const response = await globalSettingsService.getGlobalSettings();
      const settingsData = response.data || response;
      setSettings(settingsData as GlobalSettings);
    } catch (error: any) {
      console.error("Error loading settings:", error);
      toast.error(
        "Failed to load settings: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (section: string, data: any) => {
    try {
      setSaving(true);
      let response;

      switch (section) {
        case "schoolInformation":
          response = await globalSettingsService.updateSchoolInformation(data);
          break;
        case "academicYear":
          response = await globalSettingsService.updateAcademicYear(data);
          break;
        case "contactInformation":
          response = await globalSettingsService.updateContactInformation(data);
          break;
        case "financialSettings":
          response = await globalSettingsService.updateFinancialSettings(data);
          break;
        case "academicConfiguration":
          response = await globalSettingsService.updateAcademicConfiguration(
            data
          );
          break;
        case "systemSettings":
          response = await globalSettingsService.updateSystemSettings(data);
          break;
        case "notificationSettings":
          response = await globalSettingsService.updateNotificationSettings(
            data
          );
          break;
        default:
          throw new Error("Invalid section");
      }

      if (response && response.data) {
        setSettings((prev) =>
          prev ? { ...prev, [section]: response.data } : null
        );
      }
      toast.success("Settings updated successfully");
    } catch (error: any) {
      toast.error(
        "Failed to save settings: " +
          (error.response?.data?.message || error.message)
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading && !settings) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <RefreshCw className="animate-spin h-12 w-12 text-blue-600 mx-auto mb-4" />
            <span className="text-lg text-gray-600">Loading settings...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Global Settings
            </h1>
            <p className="text-gray-600 mt-1">
              Configure system-wide settings and preferences
            </p>
          </div>
          <button
            onClick={loadSettings}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <tab.icon className="w-4 h-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="space-y-6">
        {activeTab === "school" && (
          <SchoolInformationTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "academic" && (
          <AcademicYearTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "financial" && (
          <FinancialSettingsTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "academic-config" && (
          <AcademicConfigurationTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "contacts" && (
          <ContactInformationTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "system" && (
          <SystemSettingsTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "notifications" && (
          <NotificationSettingsTab
            settings={settings}
            onSave={handleSave}
            saving={saving}
          />
        )}

        {activeTab === "app-cache" && (
          <div className="bg-white rounded-lg shadow p-6">
            <ServiceWorkerSettings />
          </div>
        )}
      </div>
    </div>
  );
};

// School Information Tab Component
const SchoolInformationTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    phone: "",
    email: "",
    website: "",
    primaryColor: "#3B82F6",
    secondaryColor: "#10B981",
    theme: "light" as "light" | "dark" | "auto",
    description: "",
    mission: "",
    vision: "",
  });

  React.useEffect(() => {
    if (settings?.schoolInformation) {
      setFormData({
        name: settings.schoolInformation.name || "",
        address: settings.schoolInformation.address || "",
        phone: settings.schoolInformation.phone || "",
        email: settings.schoolInformation.email || "",
        website: settings.schoolInformation.website || "",
        primaryColor: settings.schoolInformation.primaryColor || "#3B82F6",
        secondaryColor: settings.schoolInformation.secondaryColor || "#10B981",
        theme: settings.schoolInformation.theme || "light",
        description: settings.schoolInformation.description || "",
        mission: settings.schoolInformation.mission || "",
        vision: settings.schoolInformation.vision || "",
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("schoolInformation", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <School className="w-5 h-5 mr-2 text-blue-600" />
          School Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              School Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) =>
                setFormData({ ...formData, phone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={formData.website}
              onChange={(e) =>
                setFormData({ ...formData, website: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address
            </label>
            <textarea
              value={formData.address}
              onChange={(e) =>
                setFormData({ ...formData, address: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mission
            </label>
            <textarea
              value={formData.mission}
              onChange={(e) =>
                setFormData({ ...formData, mission: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Vision
            </label>
            <textarea
              value={formData.vision}
              onChange={(e) =>
                setFormData({ ...formData, vision: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Primary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="h-10 w-20 rounded border border-gray-300"
              />
              <input
                type="text"
                value={formData.primaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, primaryColor: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Secondary Color
            </label>
            <div className="flex items-center space-x-3">
              <input
                type="color"
                value={formData.secondaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, secondaryColor: e.target.value })
                }
                className="h-10 w-20 rounded border border-gray-300"
              />
              <input
                type="text"
                value={formData.secondaryColor}
                onChange={(e) =>
                  setFormData({ ...formData, secondaryColor: e.target.value })
                }
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Theme
            </label>
            <select
              value={formData.theme}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  theme: e.target.value as "light" | "dark" | "auto",
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save School Information
        </button>
      </div>
    </form>
  );
};

// Academic Year Tab Component
const AcademicYearTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    startDate: "",
    endDate: "",
    currentYear: "",
  });

  React.useEffect(() => {
    if (settings?.academicYear) {
      setFormData(settings.academicYear);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("academicYear", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-green-600" />
          Academic Year Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Date
            </label>
            <input
              type="date"
              value={formData.startDate}
              onChange={(e) =>
                setFormData({ ...formData, startDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Date
            </label>
            <input
              type="date"
              value={formData.endDate}
              onChange={(e) =>
                setFormData({ ...formData, endDate: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Current Year Label
            </label>
            <input
              type="text"
              value={formData.currentYear}
              onChange={(e) =>
                setFormData({ ...formData, currentYear: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g., 2024-2025"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save Academic Year
        </button>
      </div>
    </form>
  );
};

// Financial Settings Tab Component
const FinancialSettingsTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    currency: "KES",
    currencySymbol: "KSh",
    paymentMethods: [] as any[],
    lateFeeEnabled: true,
    lateFeeAmount: 100,
    dueDateReminder: true,
    reminderDays: 7,
  });

  React.useEffect(() => {
    if (settings?.financialSettings) {
      setFormData(settings.financialSettings);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("financialSettings", formData);
  };

  const updatePaymentMethod = (index: number, enabled: boolean) => {
    const updated = [...formData.paymentMethods];
    updated[index] = { ...updated[index], enabled };
    setFormData({ ...formData, paymentMethods: updated });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-green-600" />
          Financial Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency
            </label>
            <input
              type="text"
              value={formData.currency}
              onChange={(e) =>
                setFormData({ ...formData, currency: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency Symbol
            </label>
            <input
              type="text"
              value={formData.currencySymbol}
              onChange={(e) =>
                setFormData({ ...formData, currencySymbol: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Payment Methods
          </h4>
          <div className="space-y-3">
            {formData.paymentMethods.map((method, index) => (
              <div key={method.id} className="flex items-center">
                <input
                  type="checkbox"
                  checked={method.enabled}
                  onChange={(e) => updatePaymentMethod(index, e.target.checked)}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  {method.name}
                </label>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Late Fee Enabled
            </label>
            <input
              type="checkbox"
              checked={formData.lateFeeEnabled}
              onChange={(e) =>
                setFormData({ ...formData, lateFeeEnabled: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          {formData.lateFeeEnabled && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Late Fee Amount
              </label>
              <input
                type="number"
                value={formData.lateFeeAmount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    lateFeeAmount: parseInt(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Due Date Reminder
            </label>
            <input
              type="checkbox"
              checked={formData.dueDateReminder}
              onChange={(e) =>
                setFormData({ ...formData, dueDateReminder: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
          </div>

          {formData.dueDateReminder && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reminder Days Before Due
              </label>
              <input
                type="number"
                value={formData.reminderDays}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    reminderDays: parseInt(e.target.value),
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save Financial Settings
        </button>
      </div>
    </form>
  );
};

// Academic Configuration Tab Component
const AcademicConfigurationTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    passingGrade: 50,
    minimumAttendance: 75,
    gradingScale: { type: "percentage" as "percentage" | "gpa", gpaScale: 4 },
    gradeBoundaries: [] as any[],
  });

  React.useEffect(() => {
    if (settings?.academicConfiguration) {
      setFormData({
        passingGrade: settings.academicConfiguration.passingGrade || 50,
        minimumAttendance:
          settings.academicConfiguration.minimumAttendance || 75,
        gradingScale: {
          type:
            settings.academicConfiguration.gradingScale.type || "percentage",
          gpaScale: settings.academicConfiguration.gradingScale.gpaScale || 4,
        },
        gradeBoundaries: settings.academicConfiguration.gradeBoundaries || [],
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("academicConfiguration", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <BookOpen className="w-5 h-5 mr-2 text-purple-600" />
          Academic Configuration
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Passing Grade (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.passingGrade}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  passingGrade: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Attendance (%)
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={formData.minimumAttendance}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  minimumAttendance: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Grading Scale
            </label>
            <select
              value={formData.gradingScale.type}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  gradingScale: {
                    ...formData.gradingScale,
                    type: e.target.value as "percentage" | "gpa",
                  },
                })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="percentage">Percentage</option>
              <option value="gpa">GPA</option>
            </select>
          </div>

          {formData.gradingScale.type === "gpa" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GPA Scale
              </label>
              <input
                type="number"
                min="1"
                max="10"
                value={formData.gradingScale.gpaScale}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    gradingScale: {
                      ...formData.gradingScale,
                      gpaScale: parseInt(e.target.value),
                    },
                  })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}
        </div>

        <div className="mt-6">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            Grade Boundaries
          </h4>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Min Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Max Score
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Points
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {formData.gradeBoundaries.map((boundary, index) => (
                  <tr key={boundary.grade}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {boundary.grade}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {boundary.minScore}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {boundary.maxScore}%
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {boundary.points}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save Academic Configuration
        </button>
      </div>
    </form>
  );
};

// Contact Information Tab Component
const ContactInformationTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    principalName: "",
    principalEmail: "",
    principalPhone: "",
    admissionsEmail: "",
    admissionsPhone: "",
    supportEmail: "",
    supportPhone: "",
  });

  React.useEffect(() => {
    if (settings?.contactInformation) {
      setFormData({
        principalName: settings.contactInformation.principalName || "",
        principalEmail: settings.contactInformation.principalEmail || "",
        principalPhone: settings.contactInformation.principalPhone || "",
        admissionsEmail: settings.contactInformation.admissionsEmail || "",
        admissionsPhone: settings.contactInformation.admissionsPhone || "",
        supportEmail: settings.contactInformation.supportEmail || "",
        supportPhone: settings.contactInformation.supportPhone || "",
      });
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("contactInformation", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Users className="w-5 h-5 mr-2 text-indigo-600" />
          Contact Information
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Principal Name
            </label>
            <input
              type="text"
              value={formData.principalName}
              onChange={(e) =>
                setFormData({ ...formData, principalName: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Principal Email
            </label>
            <input
              type="email"
              value={formData.principalEmail}
              onChange={(e) =>
                setFormData({ ...formData, principalEmail: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Principal Phone
            </label>
            <input
              type="tel"
              value={formData.principalPhone}
              onChange={(e) =>
                setFormData({ ...formData, principalPhone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admissions Email
            </label>
            <input
              type="email"
              value={formData.admissionsEmail}
              onChange={(e) =>
                setFormData({ ...formData, admissionsEmail: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Admissions Phone
            </label>
            <input
              type="tel"
              value={formData.admissionsPhone}
              onChange={(e) =>
                setFormData({ ...formData, admissionsPhone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Email
            </label>
            <input
              type="email"
              value={formData.supportEmail}
              onChange={(e) =>
                setFormData({ ...formData, supportEmail: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Support Phone
            </label>
            <input
              type="tel"
              value={formData.supportPhone}
              onChange={(e) =>
                setFormData({ ...formData, supportPhone: e.target.value })
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save Contact Information
        </button>
      </div>
    </form>
  );
};

// System Settings Tab Component
const SystemSettingsTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    maintenanceMode: {
      enabled: false,
      message:
        "System is currently under maintenance. Please check back later.",
    },
    allowRegistration: false,
    requireEmailVerification: true,
    sessionTimeout: 60,
    maxLoginAttempts: 5,
    passwordMinLength: 8,
  });

  React.useEffect(() => {
    if (settings?.systemSettings) {
      setFormData(settings.systemSettings);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("systemSettings", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Shield className="w-5 h-5 mr-2 text-red-600" />
          System Settings
        </h3>

        <div className="space-y-6">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Maintenance Mode
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.maintenanceMode.enabled}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maintenanceMode: {
                        ...formData.maintenanceMode,
                        enabled: e.target.checked,
                      },
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Enable Maintenance Mode
                </label>
              </div>

              {formData.maintenanceMode.enabled && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Maintenance Message
                  </label>
                  <textarea
                    value={formData.maintenanceMode.message}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        maintenanceMode: {
                          ...formData.maintenanceMode,
                          message: e.target.value,
                        },
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                  />
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">
              User Registration
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.allowRegistration}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      allowRegistration: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Allow User Registration
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.requireEmailVerification}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      requireEmailVerification: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Require Email Verification
                </label>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-4">
              Security Settings
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Session Timeout (minutes)
                </label>
                <input
                  type="number"
                  min="5"
                  max="480"
                  value={formData.sessionTimeout}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      sessionTimeout: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Login Attempts
                </label>
                <input
                  type="number"
                  min="1"
                  max="10"
                  value={formData.maxLoginAttempts}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      maxLoginAttempts: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Minimum Password Length
                </label>
                <input
                  type="number"
                  min="6"
                  max="20"
                  value={formData.passwordMinLength}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      passwordMinLength: parseInt(e.target.value),
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save System Settings
        </button>
      </div>
    </form>
  );
};

// Notification Settings Tab Component
const NotificationSettingsTab: React.FC<{
  settings: GlobalSettings | null;
  onSave: (section: string, data: any) => Promise<void>;
  saving: boolean;
}> = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    emailNotifications: true,
    smsNotifications: false,
    feeReminders: true,
    examReminders: true,
    attendanceAlerts: true,
    systemUpdates: true,
  });

  React.useEffect(() => {
    if (settings?.notificationSettings) {
      setFormData(settings.notificationSettings);
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave("notificationSettings", formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
          <Mail className="w-5 h-5 mr-2 text-blue-600" />
          Notification Settings
        </h3>

        <div className="space-y-4">
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Notification Channels
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.emailNotifications}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      emailNotifications: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Email Notifications
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.smsNotifications}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      smsNotifications: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  SMS Notifications
                </label>
              </div>
            </div>
          </div>

          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">
              Notification Types
            </h4>
            <div className="space-y-3">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.feeReminders}
                  onChange={(e) =>
                    setFormData({ ...formData, feeReminders: e.target.checked })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Fee Payment Reminders
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.examReminders}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      examReminders: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Exam Reminders
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.attendanceAlerts}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      attendanceAlerts: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Attendance Alerts
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.systemUpdates}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      systemUpdates: e.target.checked,
                    })
                  }
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm text-gray-700">
                  System Updates
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          disabled={saving}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
        >
          {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
          <Save className="w-4 h-4 mr-2" />
          Save Notification Settings
        </button>
      </div>
    </form>
  );
};

export default GlobalSettingsPage;
